import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Person as PersonIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { Sale, SalesSearchParams, PaymentStatus } from '../../types';
import { ColumnConfig } from '../../hooks/useColumnConfiguration';
import { useLanguage } from '../../contexts/LanguageContext';
import StatusChip from '../common/StatusChip';
import IdentityAvatar from '../common/IdentityAvatar';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import type { DataTableSort } from '../common/DataTableHeaderCell';
import { getDisplayNameInitials } from '../../utils/initials';
import { createCustomerNameActionSx } from '../../theme/interactions';

interface SalesHistoryTableProps {
  sales: Sale[];
  loading: boolean;
  error: string | null;
  searchParams: SalesSearchParams;
  columnVisibility: ColumnConfig;
  ColumnMenu: React.FC<{ columns: Array<{ key: string; label: string; condition?: boolean }> }>;
  onViewSale: (sale: Sale) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (dateString: string) => string;
  getPaymentStatusColor: (status: PaymentStatus) => 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  hasActiveFilters: boolean;
  onSortChange: (sort: string) => void;
  onPaymentStatusFilter: (status: PaymentStatus) => void;
  activePaymentStatus?: PaymentStatus;
}

export const SalesHistoryTable: React.FC<SalesHistoryTableProps> = ({
  sales,
  loading,
  error,
  searchParams,
  columnVisibility,
  ColumnMenu,
  onViewSale,
  formatCurrency,
  formatDate,
  getPaymentStatusColor,
  hasActiveFilters,
  onSortChange,
  onPaymentStatusFilter,
  activePaymentStatus,
}) => {
  const { t, language } = useLanguage();
  const sortFor = (field: string): DataTableSort => ({
    active: searchParams.sort === field,
    direction: searchParams.sort === field && searchParams.direction === 'desc' ? 'desc' : 'asc',
    onClick: () => onSortChange(field),
  });
  const visibleColumnCount = Math.max(1, Object.values(columnVisibility).filter(Boolean).length);

  return (
    <Box>
        {error && <Alert severity="error" sx={{ m: 2.5 }}>{error}</Alert>}
        <ColumnMenu
          columns={[
            { key: 'saleNumber', label: t('sales.saleNumber') },
            { key: 'customer', label: t('sales.customer') },
            { key: 'saleDate', label: t('sales.saleDate') },
            { key: 'totalAmount', label: t('sales.totalAmount') },
            { key: 'paidAmount', label: t('sales.paidAmount') },
            { key: 'remainingAmount', label: t('sales.remainingAmount') },
            { key: 'paymentStatus', label: t('sales.paymentStatus') },
            { key: 'createdBy', label: t('sales.createdBy') },
            { key: 'notes', label: t('sales.notes') },
            { key: 'actions', label: t('common.actions') },
          ]}
        />
        {loading && <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>}
        {!loading && sales.length === 0 ? (
          <Box sx={{ py: 7, px: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">{t(hasActiveFilters ? 'sales.noSearchResults' : 'sales.noSales')}</Typography>
          </Box>
        ) : (
          <>
            <Box component="ul" sx={{ display: { xs: 'block', md: 'none' }, listStyle: 'none', m: 0, mt: 1.5, p: 0 }}>
              {!loading && sales.map(sale => (
                <Box component="li" key={sale.id} sx={{ '&:not(:first-of-type)': { borderTop: '1px solid', borderColor: 'divider' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, minHeight: 76, px: 2, py: 1.25 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1, gap: .25 }}>
                      <Typography
                        component="button"
                        type="button"
                        aria-label={`${t('common.view')}: ${sale.saleNumber}`}
                        onClick={() => onViewSale(sale)}
                        sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', textAlign: 'left', font: 'inherit', fontWeight: 700, cursor: 'pointer', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2, borderRadius: .5 } }}
                      >{sale.saleNumber}</Typography>
                      <Link component={RouterLink} to={`/customers/${sale.customerId}`}
                        state={{ customerName: sale.customerName, returnTo: '/sales', companyId: searchParams.companyId }} underline="hover" noWrap sx={{ maxWidth: '100%' }}>{sale.customerName}</Link>
                      <Typography variant="caption" color="text.secondary" noWrap>{formatDate(sale.saleDate)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: .75, flexShrink: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(sale.totalAmount)}</Typography>
                      <StatusChip label={sale.paymentStatus === 'PAID' ? t('sales.paid') : sale.paymentStatus === 'PARTIAL' ? t('sales.partiallyPaid') : t('sales.unpaid')}
                        color={getPaymentStatusColor(sale.paymentStatus)} />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {columnVisibility.saleNumber && <DataTableHeaderCell sort={sortFor('saleNumber')}>{t('sales.saleNumber')}</DataTableHeaderCell>}
                    {columnVisibility.customer && <DataTableHeaderCell>{t('sales.customer')}</DataTableHeaderCell>}
                    {columnVisibility.saleDate && <DataTableHeaderCell sort={sortFor('saleDate')}>{t('sales.saleDate')}</DataTableHeaderCell>}
                    {columnVisibility.totalAmount && <DataTableHeaderCell align="right" sort={sortFor('totalAmount')}>{t('sales.totalAmount')}</DataTableHeaderCell>}
                    {columnVisibility.paidAmount && <DataTableHeaderCell align="right" sort={sortFor('paidAmount')}>{t('sales.paidAmount')}</DataTableHeaderCell>}
                    {columnVisibility.remainingAmount && <DataTableHeaderCell align="right" sort={sortFor('remainingAmount')}>{t('sales.remainingAmount')}</DataTableHeaderCell>}
                    {columnVisibility.paymentStatus && <DataTableHeaderCell sort={sortFor('paymentStatus')}>{t('sales.paymentStatus')}</DataTableHeaderCell>}
                    {columnVisibility.createdBy && <DataTableHeaderCell>{t('sales.createdBy')}</DataTableHeaderCell>}
                    {columnVisibility.notes && <DataTableHeaderCell>{t('sales.notes')}</DataTableHeaderCell>}
                    {columnVisibility.actions && <DataTableHeaderCell align="right">{t('common.actions')}</DataTableHeaderCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnCount} align="center" sx={{ py: 8 }}>
                        <CircularProgress aria-label={t('common.loading')} />
                      </TableCell>
                    </TableRow>
                  ) : sales.map(sale => (
                    <TableRow key={sale.id}>
                      {columnVisibility.saleNumber && (
                        <TableCell><Typography variant="body2" fontWeight={700} color="primary.main">{sale.saleNumber}</Typography></TableCell>
                      )}
                      {columnVisibility.customer && (
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            <IdentityAvatar>{getDisplayNameInitials(sale.customerName, language) || <PersonIcon fontSize="small" />}</IdentityAvatar>
                            <Link
                              component={RouterLink}
                              to={`/customers/${sale.customerId}`}
                              state={{ customerName: sale.customerName, returnTo: '/sales', companyId: searchParams.companyId }}
                              underline="none"
                              noWrap
                              sx={createCustomerNameActionSx}
                            >
                              {sale.customerName}
                            </Link>
                          </Box>
                        </TableCell>
                      )}
                      {columnVisibility.saleDate && <TableCell>{formatDate(sale.saleDate)}</TableCell>}
                      {columnVisibility.totalAmount && <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{formatCurrency(sale.totalAmount)}</TableCell>}
                      {columnVisibility.paidAmount && <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: sale.paidAmount > 0 ? 'success.main' : 'text.secondary' }}>{formatCurrency(sale.paidAmount)}</TableCell>}
                      {columnVisibility.remainingAmount && <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: sale.remainingAmount > 0 ? 'error.main' : 'text.secondary' }}>{formatCurrency(sale.remainingAmount)}</TableCell>}
                      {columnVisibility.paymentStatus && (
                        <TableCell>
                          <StatusChip
                            label={sale.paymentStatus === 'PAID' ? t('sales.paid') : sale.paymentStatus === 'PARTIAL' ? t('sales.partiallyPaid') : t('sales.unpaid')}
                            color={getPaymentStatusColor(sale.paymentStatus)}
                            onClick={() => onPaymentStatusFilter(sale.paymentStatus)}
                            filterActive={activePaymentStatus === sale.paymentStatus}
                          />
                        </TableCell>
                      )}
                      {columnVisibility.createdBy && <TableCell><Typography variant="body2">{sale.createdByUsername}</Typography></TableCell>}
                      {columnVisibility.notes && <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{sale.notes || '-'}</Typography></TableCell>}
                      {columnVisibility.actions && (
                        <TableCell align="right">
                          <Tooltip title={t('common.view')}>
                            <IconButton size="small" aria-label={`${t('common.view')}: ${sale.saleNumber}`} onClick={() => onViewSale(sale)}><ViewIcon /></IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
    </Box>
  );
};
